import { Typography, useMediaQuery, useTheme } from "@mui/material";
import React from "react";
import { CandlestickData, LineData, createChart, ColorType, LineStyle, IChartApi, ISeriesApi, SeriesMarker, Time, IPriceLine, UTCTimestamp, LineSeries, CandlestickSeries, createSeriesMarkers  } from 'lightweight-charts';
import { amber, blue, green, grey, orange, purple, red } from "@mui/material/colors";
import { Box, Paper } from "@mui/material";

export interface LineConfig {
    name: string,
    value: number,
    active: boolean,
}

export interface TradeConfig {
    takeProfits: LineConfig[],
    stopLoss?: number,
    buyTimestamps?: number[],
    buyPrice?: number,
    sellTimestamp?: number,
    trailingStopLoss?: number
    sellPrice?: number,
}

interface ComponentProps {
    symbolString: string;
    data: CandlestickData[];
    precision: number,
    buyTimestamp?: number,
    buyPrice?: number,
    sellTimestamp?: number,
    takeProfit1?: number,
    takeProfit2?: number,
    takeProfit3?: number,
    stopLoss?: number,
    trailingStopLoss?: number
    tradeConfig: TradeConfig | null,
    height?: number | undefined;
    showAsPercent?: boolean;
    showEMA?: boolean;
    /** If provided, all y-axis values and lines will be calculated relative to this value */
    relativeToBuyPrice?: boolean;
    //data: LineData[];
}

function mapCandlestickToLineData(candle: CandlestickData): LineData {
    return {
        time: candle.time,
        value: candle.close, // Typically, EMA is based on the close price
        //color: candle.color // Using the candle's color for the line data, but you can adjust as needed
    };
}

function addTenSecondsToTime(time: Time): Time {
    const TEN_SECONDS = 10 * 1000;

    if (isUTCTimestamp(time)) {
        return (time as any + TEN_SECONDS) as UTCTimestamp;
    } else if (typeof time === 'string') {
        let date = new Date(time);
        date.setSeconds(date.getSeconds() + 10);
        return date.toISOString();
    } else {
        // For BusinessDay, we're not adding 10 seconds as it doesn't make sense in this context.
        // You might want to handle this differently based on your requirements.
        return time;
    }
}

function isUTCTimestamp(time: Time): time is UTCTimestamp {
    return typeof time === 'number';
}

/*
function fillMissingKlines(data: CandlestickData[]): CandlestickData[] {
    if (data.length === 0) return [];

    const TEN_SECONDS = 10 * 1000; // 10 seconds in milliseconds

    // 1. Build an array of expected times
    let startTime = data[0].time as any as UTCTimestamp;
    let endTime = data[data.length - 1].time as any as UTCTimestamp;
    let expectedTimes: UTCTimestamp[] = [];

    for (let time = startTime as any; time <= endTime as any; time += 10) {
        expectedTimes.push(time);
    }

    // 2. Find missing times
    let actualTimes = data.map(d => d.time as any as UTCTimestamp);
    let missingTimes = expectedTimes.filter(time => !actualTimes.includes(time));

    // 3. Populate the missing times with data
    let missingData: CandlestickData[] = missingTimes.map(time => {
        let lastKnownRecord = data.slice().reverse().find(record => record.time as any < time);
        let lastClose = lastKnownRecord ? lastKnownRecord.close : data[0].close;

        return {
            time: time,
            open: lastClose,
            high: lastClose,
            low: lastClose,
            close: lastClose
        };
    });

    // 4. Merge the original and new data arrays
    let mergedData = [...data, ...missingData];

    // 5. Sort the merged array by time
    // mergedData.sort((a, b) => (a.time as any) - (b.time as any));

    // 6. Remove any time duplicates (first or last, doesn't matter)
    let uniqueData = new Map<UTCTimestamp, CandlestickData>();
    mergedData.forEach(item => {
        if (!uniqueData.has(item.time as any)) {
            uniqueData.set(item.time as any, item);
        }
    });

    mergedData = Array.from(uniqueData.values());
    mergedData.sort((a, b) => (a.time as any) - (b.time as any)); // Sort again to ensure order

    return mergedData;
}
*/

function fillMissingKlines(data: CandlestickData[]): CandlestickData[] {
    if (data.length === 0) return [];

    const TEN_SECONDS = 10; // UTCTimestamp is in seconds

    let startTime = data[0].time as any as UTCTimestamp;
    let endTime = data[data.length - 1].time as any as UTCTimestamp;

    // Preprocess into a map for fast lookup
    const timeToData = new Map<UTCTimestamp, CandlestickData>();
    for (const d of data) {
        timeToData.set(d.time as any as UTCTimestamp, d);
    }

    let result: CandlestickData[] = [];
    let lastClose = data[0].close;

    for (let time = startTime as any; time <= endTime as any; time += TEN_SECONDS) {
        const existing = timeToData.get(time);
        if (existing) {
            result.push(existing);
            lastClose = existing.close;
        } else {
            result.push({
                time: time,
                open: lastClose,
                high: lastClose,
                low: lastClose,
                close: lastClose
            });
        }
    }

    return result;
}

function computeEMA(data: CandlestickData[], period: number, color?: string): LineData[] {
    if (data.length === 0) return [];

    let multiplier = 2 / (period + 1);
    let emaValues: LineData[] = [];
    let previousEMA = data[0].close; // Starting with the first data point as the initial EMA value

    emaValues.push({
        time: data[0].time,
        value: previousEMA,
        color: color
    });

    for (let i = 1; i < data.length; i++) {
        let currentEMA = ((data[i].close - previousEMA) * multiplier) + previousEMA;
        emaValues.push({
            time: data[i].time,
            value: currentEMA,
            color: color
        });
        previousEMA = currentEMA;
    }

    return emaValues;
}

function SymbolPriceTradingViewChartMemo(props: ComponentProps) {

    const chartContainerRef = React.useRef<HTMLDivElement>(null);

    const chartRef = React.useRef<IChartApi | null>(null);
    const seriesRef = React.useRef<ISeriesApi<"Candlestick"> | null>(null);
    const ema1Ref = React.useRef<ISeriesApi<"Line"> | null>(null);
    const ema2Ref = React.useRef<ISeriesApi<"Line"> | null>(null);
    const ema3Ref = React.useRef<ISeriesApi<"Line"> | null>(null);
    const prevDataLenRef = React.useRef<number>(0);

    const [tradeConfig, setTradeConfig] = React.useState<TradeConfig | null>(props.tradeConfig);

    const [showActiveTPOnly, setShowActiveTPOnly] = React.useState<boolean>(true);

    // Check if we are using a small screen device
    let isSmallScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

    // ResizeObserver instead of per-tick handleResize
    React.useEffect(() => {
        const container = chartContainerRef.current;
        if (!container) return;
        const ro = new ResizeObserver((entries) => {
            const chart = chartRef.current;
            if (chart && entries[0]) {
                chart.applyOptions({ width: entries[0].contentRect.width });
            }
        });
        ro.observe(container);
        return () => ro.disconnect();
    }, []);

    const priceLines = React.useRef<IPriceLine[]>([]);

    // Memoize the relativeToBuyPrice transformation so it's only recomputed when data or buyPrice changes
    const workingData = React.useMemo(() => {
        if (!props.data || props.data.length === 0) return [];

        // Deduplicate by time (last-write-wins)
        const map = new Map<Time, CandlestickData>();
        for (const r of props.data) map.set(r.time, r);
        let deduped = Array.from(map.values());

        if (props.relativeToBuyPrice) {
            const buyPrice = props.tradeConfig?.buyPrice || 1.0;
            return deduped.map(d => ({
                time: d.time,
                open: ((d.open / buyPrice) - 1.0) * 100.0,
                high: ((d.high / buyPrice) - 1.0) * 100.0,
                low: ((d.low / buyPrice) - 1.0) * 100.0,
                close: ((d.close / buyPrice) - 1.0) * 100.0
            }));
        }

        return deduped;
    }, [props.data, props.relativeToBuyPrice, props.tradeConfig?.buyPrice]);
    //const technicalIndicatorLines

    React.useEffect(
		() => {

            if (chartContainerRef.current !== null && chartRef.current === null && workingData.length > 0)
            {
                // Clear the chartContainerRef
                chartContainerRef.current.innerHTML = '';

                // Build a new chart
                let tempChart = createChart(chartContainerRef.current, {
                    layout: {
                        background: { type: ColorType.Solid, color: "transparent" },
                        textColor: grey[50],
                        attributionLogo: false,
                        //textColor,
                    },
                    grid: {
                        vertLines: {
                            style: LineStyle.SparseDotted,
                            color: grey[400],
                        },
                        horzLines: {
                            visible: false
                        }
                    },
                    timeScale: {
                        fixRightEdge: true,
                        //rightOffset: 20,
                        timeVisible: true,
                        barSpacing: chartContainerRef.current.clientWidth / 90.0,
                    },
                    rightPriceScale: {
                        //autoScale: true, // disables auto scaling based on visible content
                        scaleMargins: {
                            top: 0.1,
                            bottom: 0.1,
                        },
                        
                        //borderColor: '#ff44ee',
                        mode: props.showAsPercent ? 2 : 0,
                        
                    },
                    width: chartContainerRef.current.clientWidth,
                    height: props.height || (isSmallScreen ? 256 : 384),
                    localization: {
                      //  priceFormatter: 'price',
                    },
                    
                });

                // Show all available data by default
                tempChart.timeScale().fitContent();

                let gapFreeData = fillMissingKlines(workingData);

                if (props.showEMA) {
                    let tempTechnicalIndicators = tempChart.addSeries(LineSeries, 
                        {
                            color: blue[500],
                            lineWidth: 1,
                            lastValueVisible: false,
                            //title: "EMA (1m)",
                            priceLineVisible: false,
                            priceFormat: {
                                type: props.showAsPercent || props.relativeToBuyPrice ? 'percent' : 'price',
                                precision: props.precision,
                                minMove: Math.max(0.00000001, 1.0 / Math.pow(10.0, (props.precision))),
                            },
                        });

                    tempTechnicalIndicators.setData(computeEMA(gapFreeData, 6, blue[500]));

                    let tempTechnicalIndicators2 = tempChart.addSeries(LineSeries, 
                        {
                            color: purple[500],
                            lineWidth: 1,
                            lastValueVisible: false,
                            //title: "EMA (2m)",
                            priceLineVisible: false,
                            priceFormat: {
                                type: props.showAsPercent || props.relativeToBuyPrice ? 'percent' : 'price',
                                precision: props.precision,
                                minMove: Math.max(0.00000001, 1.0 / Math.pow(10.0, (props.precision))),
                            },
                        });

                    tempTechnicalIndicators2.setData(computeEMA(gapFreeData, 18, purple[500]));

                    let tempTechnicalIndicators3 = tempChart.addSeries(LineSeries, 
                        {
                            color: amber[500],
                            lineWidth: 1,
                            lastValueVisible: false,
                            //title: "EMA (3m)",
                            priceLineVisible: false,
                            priceFormat: {
                                type: props.showAsPercent || props.relativeToBuyPrice ? 'percent' : 'price',
                                precision: props.precision,
                                minMove: Math.max(0.00000001, 1.0 / Math.pow(10.0, (props.precision))),

                            }, 
                        });

                    tempTechnicalIndicators3.setData(computeEMA(gapFreeData, 30, amber[500]));
                    
                    ema1Ref.current = tempTechnicalIndicators;
                    ema2Ref.current = tempTechnicalIndicators2;
                    ema3Ref.current = tempTechnicalIndicators3;
                    }
                // Add this last so that it's in front
                let tempSeries = tempChart.addSeries(CandlestickSeries, {
                    wickUpColor: green[500],
                    upColor: green[500],
                    wickDownColor: red[500],
                    downColor: red[500],
                    borderVisible: false,
                    
                    priceFormat: {
                        type: props.showAsPercent || props.relativeToBuyPrice ? 'percent' : 'price',
                        precision: props.precision,
                        minMove: Math.max(0.00000001, 1.0 / Math.pow(10.0, (props.precision))),
                    },
                    priceLineVisible: true,
                });

                tempSeries.setData(gapFreeData);

                  chartRef.current = tempChart;
                  seriesRef.current = tempSeries;
                  prevDataLenRef.current = workingData.length;
            }
			
            return;
		},
		[workingData]
	);

    // Trade config overlay (price lines + markers)
    React.useEffect(() => {

        const series = seriesRef.current;
        if (!series) return;

            // Update the config
            setTradeConfig(props.tradeConfig);

            // Remove old price lines
            priceLines.current.forEach(t => {
                series.removePriceLine(t);
            });
            priceLines.current = [];
            
            // Add the new config
            props.tradeConfig?.takeProfits.forEach(tp => {

                if (showActiveTPOnly && !tp.active) {
                    return;
                }

                priceLines.current.push(series.createPriceLine({
                    price: props.relativeToBuyPrice ? (tp.value / (props.tradeConfig?.buyPrice || 1.0) - 1.0) * 100.0 : tp.value,
                    color: tp.active ? blue[500] : blue[900],
                    lineWidth: 2,
                    lineStyle: LineStyle.Dotted,
                    lineVisible: true,
                    axisLabelVisible: true,
                    title: tp.name,
                    //draggable: true,
                }));
            });

            if (props.tradeConfig?.buyPrice) {
                priceLines.current.push(series.createPriceLine({
                    price: props.relativeToBuyPrice ? 0.0 : props.tradeConfig?.buyPrice,
                    color: grey[500],
                    lineWidth: 2,
                    lineStyle: LineStyle.Dotted,
                    lineVisible: true,
                    axisLabelVisible: true,
                    title: 'Open',
                    //draggable: true,
                }));
            }

            if (props.tradeConfig?.sellPrice) {
                priceLines.current.push(series.createPriceLine({
                    price: props.relativeToBuyPrice ? (props.tradeConfig?.sellPrice / (props.tradeConfig?.buyPrice || 1.0) - 1.0) * 100.0 : props.tradeConfig?.sellPrice,
                    color: grey[500],
                    lineWidth: 2,
                    lineStyle: LineStyle.Dotted,
                    lineVisible: true,
                    axisLabelVisible: true,
                    title: 'Close',
                    //draggable: true,
                }));
            }
            
            if (props.tradeConfig?.stopLoss) {
                priceLines.current.push(series.createPriceLine({
                    price: props.relativeToBuyPrice ? (props.tradeConfig?.stopLoss / (props.tradeConfig?.buyPrice || 1.0) - 1.0) * 100.0 : props.tradeConfig?.stopLoss,
                color: orange[500],
                lineWidth: 2,
                lineStyle: LineStyle.Dotted,
                lineVisible: true,
                axisLabelVisible: true,
                title: 'SL',
                
            }));
        }

            if (props.tradeConfig?.trailingStopLoss) {
                priceLines.current.push(series.createPriceLine({
                price: props.relativeToBuyPrice ? (props.tradeConfig?.trailingStopLoss / (props.tradeConfig?.buyPrice || 1.0) - 1.0) * 100.0 : props.tradeConfig?.trailingStopLoss,
                color: orange[500],
                lineWidth: 2,
                lineStyle: LineStyle.Dotted,
                lineVisible: true,
                axisLabelVisible: true,
                title: 'TSL',
                //draggable: true,
                }));
        }
                
            const markers:SeriesMarker<Time>[] = [];

            if ((props.tradeConfig?.buyTimestamps || []).length > 0) {
                (props.tradeConfig?.buyTimestamps || []).forEach((t, i) => {
                    markers.push({
                        time: Math.floor(Math.floor(t / 10000.0) * 10.0) as Time,
                        position: 'belowBar',
                        color: i == 0 ? grey[200] : grey[300],
                        shape: 'arrowUp',
                        text: i == 0 ? 'Open' : '+Open',
                    });
                });
            }

            if (props.tradeConfig?.sellTimestamp) {
                markers.push({
                    time: Math.floor(Math.floor((props.tradeConfig?.sellTimestamp || 0.0) / 10000.0) * 10.0) as Time,
                    position: 'aboveBar',
                    color: orange[700],
                    shape: 'arrowDown',
                    text: 'Close',
                })
            }

            //newSeries.setMarkers(markers);
            const seriesMarkers = createSeriesMarkers(series, markers);
    }, [tradeConfig, workingData])
    
    // Shallow comparison for tradeConfig changes (replaces JSON.stringify)
    React.useEffect(
		() => {
            if (!seriesRef.current) return;

            const prev = tradeConfig;
            const next = props.tradeConfig;

            // Quick shallow check — if reference changed, update
            if (prev !== next) {
                setTradeConfig(next);
            }
		},
		[props.tradeConfig]
	);

    // Incremental data update effect — uses update() for appended bars, setData() only when history changes
    React.useEffect(
		() => {
            const series = seriesRef.current;
            if (!series || workingData.length === 0) return;

            const prevLen = prevDataLenRef.current;

            // If only new bars were appended (common case), use incremental update
            if (workingData.length > prevLen && prevLen > 0) {
                // Update the last bar that was previously set (in case it changed) + append new ones
                const startIdx = Math.max(0, prevLen - 1);
                const newBars = workingData.slice(startIdx);

                // Fill gaps in just the new portion by doing a targeted gap-fill
                const gapFreeTail = fillMissingKlines(newBars);
                for (const bar of gapFreeTail) {
                    series.update(bar);
                }

                if (props.showEMA && gapFreeTail.length > 0) {
                    // We need a window of recent data for EMA — use the tail of workingData
                    const emaWindow = workingData.slice(-Math.max(31, workingData.length));
                    const gapFreeWindow = fillMissingKlines(emaWindow);

                    if (gapFreeWindow.length >= 31) {
                        const emaSlice = gapFreeWindow.slice(-31);

                        if (ema1Ref.current) {
                            const tempEMA = computeEMA(emaSlice, 6, blue[500]);
                            ema1Ref.current.update(tempEMA[tempEMA.length - 1]);
                        }
                        if (ema2Ref.current) {
                            const tempEMA = computeEMA(emaSlice, 18, purple[500]);
                            ema2Ref.current.update(tempEMA[tempEMA.length - 1]);
                        }
                        if (ema3Ref.current) {
                            const tempEMA = computeEMA(emaSlice, 30, amber[500]);
                            ema3Ref.current.update(tempEMA[tempEMA.length - 1]);
                        }
                    }
                }
            }
            else if (prevLen > 0) {
                // History changed (e.g. data was replaced or trimmed) — full setData needed
                const gapFreeData = fillMissingKlines(workingData);
                series.setData(gapFreeData);

                if (props.showEMA) {
                    if (ema1Ref.current) ema1Ref.current.setData(computeEMA(gapFreeData, 6, blue[500]));
                    if (ema2Ref.current) ema2Ref.current.setData(computeEMA(gapFreeData, 18, purple[500]));
                    if (ema3Ref.current) ema3Ref.current.setData(computeEMA(gapFreeData, 30, amber[500]));
                }
            }

            prevDataLenRef.current = workingData.length;
		},
		[workingData]
	);

  return (
    <Box sx={{ width: '100%', height: isSmallScreen ? '50%' : '100%', p:1, mt: 2  }}>
        {
            props.showEMA 
            ?<Typography variant="body2" sx={{m: 0, p: 0}}>
                <Typography component="span"  variant="body2" sx={{m: 0, p: 0, fontSize: '0.75em', color: blue[500]}}>
                EMA (1m) &nbsp;
                </Typography>
                <Typography component="span"  variant="body2" sx={{m: 0, p: 0, fontSize: '0.75em', color: purple[500]}}>
                EMA (3m) &nbsp;
                </Typography>
                <Typography component="span"  variant="body2" sx={{m: 0, p: 0, fontSize: '0.75em', color: amber[500]}}>
                EMA (5m) &nbsp;
                </Typography>
            </Typography>
            : null
        }
        
        <div style={{ width: '100%', height: isSmallScreen ? '50%' : '100%' }}>
            <div
                ref={chartContainerRef}
            />
        </div>
    </Box>
  );
}

export default React.memo(SymbolPriceTradingViewChartMemo);
